/**
 * Template Detail Modal - Full details, Similar Templates, and Success Stories
 * Opens when a user clicks a template card in the gallery.
 */
import React, { useState, useEffect } from 'react';
import {
  X, TrendingDown, DollarSign, Calendar, Star, Users, Sparkles,
  ArrowRight, Award, Target, Building2, Loader2, CheckCircle, Tag, Scale, Plus, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { apiFetch } from '../../services/api_service';


const DIFF_STYLE = {
  beginner:     ['text-emerald-400', 'bg-emerald-500/15', 'border-emerald-500/30'],
  intermediate: ['text-amber-400',   'bg-amber-500/15',   'border-amber-500/30'],
  advanced:     ['text-red-400',     'bg-red-500/15',     'border-red-500/30'],
};

function fmt$(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}

export default function TemplateDetailModal({ template: t, onClose, onUse, onAddToCompare, isInCompare }) {
  const [similar, setSimilar] = useState([]);
  const [stories, setStories] = useState([]);
  const [storiesMock, setStoriesMock] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(true);
  const [loadingStories, setLoadingStories] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const fetchSimilar = async () => {
      try {
        const d = await apiFetch(`/v1/simulations/templates/similar/${t.templateId}?limit=6`);
        if (d && !d._error) {
          setSimilar(d.similarTemplates || []);
        }
      } catch { /* ignore */ }
      setLoadingSimilar(false);
    };
    const fetchStories = async () => {
      try {
        const d = await apiFetch(`/v1/simulations/templates/success-stories/${t.templateId}`);
        if (d && !d._error) {
          setStories(d.successStories || []);
          setStoriesMock(!!d.isMockData);
        }
      } catch { /* ignore */ }
      setLoadingStories(false);
    };
    fetchSimilar();
    fetchStories();
  }, [t.templateId]);

  const er = t.expectedResults || {};
  const costRange = er.financialSummary?.totalCostRange;
  const bench = t.industryBenchmark || {};
  const [textC, bgC, borderC] = DIFF_STYLE[t.difficulty] || DIFF_STYLE.intermediate;

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="template-detail-modal">
      <div className="bg-[#0C0C0C] border border-[#1F1F1F] rounded-2xl max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="shrink-0 bg-gradient-to-r from-violet-900/20 to-blue-900/15 border-b border-[#1F1F1F] px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className={`${bgC} ${textC} border ${borderC} text-[10px]`}>{t.difficulty}</Badge>
                <Badge className="bg-[#1A1A1A] text-[#A0A0A0] border border-[#2A2A2A] text-[10px]">{t.industry.replace(/_/g, ' ')}</Badge>
                {er.emissionsReduction?.percentageReduction >= 80 && (
                  <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px]">
                    <CheckCircle className="w-3 h-3 mr-1 inline" />Near Net-Zero
                  </Badge>
                )}
              </div>
              <h2 className="text-lg font-bold text-white mb-1" data-testid="modal-template-name">{t.templateName}</h2>
              <p className="text-xs text-[#6B6B6B] leading-relaxed">{t.templateDescription}</p>
              <div className="flex items-center gap-4 mt-2 text-[10px]">
                <span className="flex items-center gap-1 text-amber-400"><Star className="w-3 h-3 fill-amber-400" /> {t.avgRating?.toFixed(1)}</span>
                <span className="flex items-center gap-1 text-[#6B6B6B]"><Users className="w-3 h-3" /> {(t.popularity || 0).toLocaleString()} companies</span>
                {bench.peerAdoptionRate && <span className="text-[#6B6B6B]">{Math.round(bench.peerAdoptionRate * 100)}% peer adoption</span>}
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1F1F1F] transition-colors shrink-0" data-testid="close-detail-modal">
              <X className="w-5 h-5 text-[#6B6B6B]" />
            </button>
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────── */}
        <div className="shrink-0 border-b border-[#1F1F1F] px-6 flex gap-1">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'stories', label: `Success Stories${stories.length ? ` (${stories.length})` : ''}` },
            { id: 'similar', label: `Similar${similar.length ? ` (${similar.length})` : ''}` },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                tab === id ? 'border-violet-500 text-violet-400' : 'border-transparent text-[#6B6B6B] hover:text-[#A0A0A0]'
              }`}
              data-testid={`tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Content ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'overview' && <OverviewTab t={t} er={er} bench={bench} costRange={costRange} />}
          {tab === 'stories' && <StoriesTab stories={stories} isMock={storiesMock} loading={loadingStories} />}
          {tab === 'similar' && <SimilarTab similar={similar} loading={loadingSimilar} onUse={onUse} />}
        </div>

        {/* ── Footer ───────────────────────────────────────────── */}
        <div className="shrink-0 border-t border-[#1F1F1F] px-6 py-4 flex items-center justify-between bg-[#0A0A0A]">
          <div className="flex items-center gap-6 text-xs">
            <div>
              <p className="text-[#4A4A4A] mb-0.5">Reduction</p>
              <p className="text-base font-bold text-emerald-400">{er.emissionsReduction?.percentageReduction || 0}%</p>
            </div>
            <div>
              <p className="text-[#4A4A4A] mb-0.5">Investment</p>
              <p className="text-base font-bold text-blue-400">{costRange ? `${fmt$(costRange.min)}-${fmt$(costRange.max)}` : 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#4A4A4A] mb-0.5">Target</p>
              <p className="text-base font-bold text-purple-400">{t.target?.targetYear || '2030'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-[#2A2A2A] text-[#A0A0A0]" onClick={onClose}>Close</Button>
            {onAddToCompare && (
              <Button
                variant="outline"
                size="sm"
                className={`gap-1.5 ${isInCompare ? 'border-violet-500/30 text-violet-400 bg-violet-500/10' : 'border-[#2A2A2A] text-[#A0A0A0] hover:text-violet-400 hover:border-violet-500/30'}`}
                onClick={() => onAddToCompare(t.templateId)}
                data-testid="modal-add-compare-btn"
              >
                {isInCompare ? <Check className="w-3.5 h-3.5" /> : <Scale className="w-3.5 h-3.5" />}
                {isInCompare ? 'In Comparison' : 'Add to Compare'}
              </Button>
            )}
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5" onClick={() => onUse(t.templateId)} data-testid="modal-use-template-btn">
              <Sparkles className="w-3.5 h-3.5" /> Use This Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Overview Tab ────────────────────────────────────────────────────────── */
function OverviewTab({ t, er, bench, costRange }) {
  return (
    <div className="space-y-5">
      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 gap-3">
        <MetricCard icon={<TrendingDown className="w-4 h-4 text-emerald-400" />} label="Emissions Reduction" value={`${er.emissionsReduction?.percentageReduction || 0}%`} accent="emerald" />
        <MetricCard icon={<DollarSign className="w-4 h-4 text-blue-400" />} label="Investment Range" value={costRange ? `${fmt$(costRange.min)} - ${fmt$(costRange.max)}` : 'N/A'} accent="blue" />
        <MetricCard icon={<Calendar className="w-4 h-4 text-purple-400" />} label="Target Year" value={t.target?.targetYear || '2030'} accent="purple" />
        <MetricCard icon={<Target className="w-4 h-4 text-amber-400" />} label="Reduction Target" value={`${t.target?.targetValue || 0}%`} accent="amber" />
      </div>

      {/* Industry Benchmark */}
      {bench.peerAdoptionRate && (
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[#A0A0A0] mb-3 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-blue-400" /> Industry Benchmark
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-[#4A4A4A] mb-1.5">Peer Adoption Rate</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full" style={{ width: `${bench.peerAdoptionRate * 100}%` }} />
                </div>
                <span className="text-xs font-bold text-white">{Math.round(bench.peerAdoptionRate * 100)}%</span>
              </div>
            </div>
            {bench.avgInvestment && (
              <div>
                <p className="text-[10px] text-[#4A4A4A] mb-1.5">Avg. Industry Investment</p>
                <p className="text-sm font-bold text-white">{fmt$(bench.avgInvestment)}</p>
              </div>
            )}
          </div>
          {bench.leadingCompanies?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#1F1F1F]">
              <p className="text-[10px] text-[#4A4A4A] mb-2">Leading companies using this approach</p>
              <div className="flex flex-wrap gap-1.5">
                {bench.leadingCompanies.map((c, i) => (
                  <Badge key={i} className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px]">{c}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {t.tags?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[#A0A0A0] mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Tags</h3>
          <div className="flex flex-wrap gap-1.5">
            {t.tags.map(tag => (
              <Badge key={tag} className="bg-[#1A1A1A] text-[#6B6B6B] border border-[#2A2A2A] text-[10px]">{tag.replace(/_/g, ' ')}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Template Details */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[#A0A0A0] mb-2">Use Case</h3>
          <p className="text-sm text-white capitalize">{(t.useCase || '').replace(/_/g, ' ')}</p>
        </div>
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[#A0A0A0] mb-2">Sub-Industry</h3>
          <p className="text-sm text-white capitalize">{(t.subIndustry || 'general').replace(/_/g, ' ')}</p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, accent }) {
  const border = { emerald: 'border-emerald-500/20', blue: 'border-blue-500/20', purple: 'border-purple-500/20', amber: 'border-amber-500/20' };
  return (
    <div className={`bg-[#111] border ${border[accent]} rounded-xl p-3 text-center`}>
      <div className="flex justify-center mb-1">{icon}</div>
      <p className="text-sm font-bold text-white">{value}</p>
      <p className="text-[9px] text-[#4A4A4A]">{label}</p>
    </div>
  );
}

/* ─── Stories Tab ─────────────────────────────────────────────────────────── */
function StoriesTab({ stories, isMock, loading }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>;

  if (stories.length === 0) {
    return (
      <div className="text-center py-12">
        <Award className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
        <p className="text-sm text-[#6B6B6B]">No success stories yet for this template</p>
        <p className="text-[10px] text-[#4A4A4A] mt-1">Be the first to implement and share your results!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isMock && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2 text-[10px] text-blue-400">
          These are illustrative examples from similar implementations. Real success stories will appear as companies adopt this template.
        </div>
      )}
      {stories.map((story, i) => <StoryCard key={story.storyId || i} story={story} />)}
    </div>
  );
}

function StoryCard({ story }) {
  const results = story.resultsAchieved || {};
  const fin = results.financialResults || {};
  const tl = results.timelineResults || {};
  const testimonial = story.testimonial || {};
  const sizeLabel = { enterprise: 'Enterprise', mid_market: 'Mid-Market', small: 'Small Business' }[story.companySize] || story.companySize;

  return (
    <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-5 hover:border-emerald-500/20 transition-colors" data-testid="story-card">
      {/* Company header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {story.companyName[0]}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{story.companyName}</p>
            <p className="text-[10px] text-[#4A4A4A]">
              {sizeLabel} &middot; {fmt$(story.companyRevenue)} revenue &middot; {story.companyIndustry?.replace(/_/g, ' ')}
            </p>
          </div>
        </div>
        <Badge className="bg-[#1A1A1A] text-[#6B6B6B] border border-[#2A2A2A] text-[10px] shrink-0">
          {story.implementationYear}-{story.completionYear}
        </Badge>
      </div>

      {/* Results */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-[#0A0A0A] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#4A4A4A] mb-0.5">Emissions Reduced</p>
          <p className="text-lg font-bold text-emerald-400">{results.emissionsReduction}%</p>
          <p className="text-[9px] text-[#4A4A4A]">vs {results.vsTemplateExpected}% expected</p>
        </div>
        <div className="bg-[#0A0A0A] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#4A4A4A] mb-0.5">Actual Cost</p>
          <p className="text-lg font-bold text-blue-400">{fmt$(fin.actualCost || 0)}</p>
          <p className="text-[9px] text-[#4A4A4A]">{fin.paybackPeriod}yr payback</p>
        </div>
        <div className="bg-[#0A0A0A] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#4A4A4A] mb-0.5">Timeline</p>
          <p className="text-lg font-bold text-purple-400">{tl.actualTimeline} mo</p>
          <p className="text-[9px] text-[#4A4A4A]">vs {tl.vsTemplateExpected} mo expected</p>
        </div>
      </div>

      {/* Testimonial */}
      {testimonial.quote && (
        <blockquote className="border-l-2 border-violet-500/40 pl-3 mb-3">
          <p className="text-xs text-[#A0A0A0] italic leading-relaxed">"{testimonial.quote}"</p>
          <p className="text-[10px] text-[#4A4A4A] mt-1">- {testimonial.author}, {testimonial.title}</p>
        </blockquote>
      )}

      {/* Key Learnings */}
      {story.keyLearnings?.length > 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-lg p-3">
          <p className="text-[10px] font-semibold text-emerald-400 mb-1.5">Key Learnings</p>
          <ul className="space-y-1">
            {story.keyLearnings.map((l, i) => (
              <li key={i} className="text-[11px] text-[#A0A0A0] flex items-start gap-1.5">
                <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /> {l}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Challenges */}
      {story.challenges?.length > 0 && (
        <div className="mt-3 space-y-2">
          {story.challenges.map((ch, i) => (
            <div key={i} className="bg-[#0A0A0A] rounded-lg p-2.5">
              <p className="text-[10px] text-amber-400 font-medium mb-0.5">Challenge: {ch.challenge}</p>
              <p className="text-[10px] text-emerald-400">Solution: {ch.solution}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Similar Tab ─────────────────────────────────────────────────────────── */
function SimilarTab({ similar, loading, onUse }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>;

  if (similar.length === 0) {
    return (
      <div className="text-center py-12">
        <Sparkles className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
        <p className="text-sm text-[#6B6B6B]">No similar templates found in this industry</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-[#4A4A4A]">Based on difficulty, tags, use case, and cost similarity</p>
      <div className="grid grid-cols-2 gap-3">
        {similar.map(s => <SimilarCard key={s.templateId} s={s} onUse={onUse} />)}
      </div>
    </div>
  );
}

function SimilarCard({ s, onUse }) {
  const er = s.expectedResults || {};
  const costRange = er.financialSummary?.totalCostRange;
  const [textC, bgC, borderC] = DIFF_STYLE[s.difficulty] || DIFF_STYLE.intermediate;

  return (
    <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-4 hover:border-violet-500/30 transition-all group" data-testid={`similar-${s.templateId}`}>
      <div className="flex items-center justify-between mb-2">
        <Badge className={`${bgC} ${textC} border ${borderC} text-[10px]`}>{s.difficulty}</Badge>
        {s.similarityScore != null && (
          <span className="text-[10px] text-violet-400 font-medium">{s.similarityScore}pt match</span>
        )}
      </div>
      <h4 className="text-xs font-semibold text-white mb-1 group-hover:text-violet-400 transition-colors line-clamp-2">{s.templateName}</h4>

      {/* Match reasons */}
      {s.matchReasons?.length > 0 && (
        <ul className="mb-2">
          {s.matchReasons.slice(0, 2).map((r, i) => (
            <li key={i} className="text-[10px] text-[#6B6B6B] flex items-start gap-1">
              <span className="text-violet-400">-</span> {r}
            </li>
          ))}
        </ul>
      )}

      {/* Metrics row */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-center">
        <div className="bg-[#0A0A0A] rounded-lg py-1.5">
          <p className="text-xs font-bold text-emerald-400">{er.emissionsReduction?.percentageReduction || 0}%</p>
          <p className="text-[8px] text-[#4A4A4A]">Reduction</p>
        </div>
        <div className="bg-[#0A0A0A] rounded-lg py-1.5">
          <p className="text-xs font-bold text-blue-400">{costRange ? fmt$(costRange.min) : 'N/A'}</p>
          <p className="text-[8px] text-[#4A4A4A]">Investment</p>
        </div>
      </div>

      <button
        onClick={() => onUse(s.templateId)}
        className="w-full py-1.5 rounded-lg bg-[#1A1A1A] text-[#A0A0A0] text-[10px] font-medium hover:bg-violet-600 hover:text-white transition-colors flex items-center justify-center gap-1"
        data-testid={`use-similar-${s.templateId}`}
      >
        Use Template <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
